export const providerFormContent = {
  title: 'Provedores de IA',
  description:
    'Gerencie as chaves usadas pelo Kraftchat para gerar respostas automáticas.',
  addTitle: 'Adicionar novo provedor',
  addDescription:
    'Conecte um novo provedor informando a chave API e o modelo padrão.',
  providerLabel: 'Provedor',
  modelLabel: 'Modelo padrão',
  apiKeyLabel: 'Chave API',
  apiKeyHelp:
    'A chave é salva no backend e será exibida apenas de forma mascarada.',
  editApiKeyHelp:
    'Deixe em branco para manter a chave atual. Informe uma nova chave para substituir.',
  connectLabel: 'Conectar provedor',
  saveLabel: 'Salvar alterações',
  savingLabel: 'Salvando...',
  cancelLabel: 'Cancelar',
  deleteSuccessMessage: 'Provedor removido com sucesso.',
  apiKeyRequiredError: 'Informe a chave API para conectar este provedor.',
  fallbackError: 'Não foi possível salvar o provedor.',
  deleteFallbackError: 'Não foi possível excluir o provedor.',
  modelPlaceholder: 'Ex: gpt-4o-mini',
  apiKeyPlaceholder: 'Cole a chave API do provedor',
  listTitle: 'Provedores cadastrados',
  listDescription:
    'Edite ou remova provedores já conectados. As chaves aparecem apenas mascaradas.',
  emptyListTitle: 'Nenhum provedor conectado ainda.',
  noAvailableProviders: 'Todos os provedores disponíveis já foram cadastrados.',
  currentKeyLabel: 'Chave',
  modelFallback: 'Modelo não definido',
  editLabel: 'Editar',
  deleteLabel: 'Excluir',
  confirmDeleteLabel: 'Confirmar',
  deleteConfirmHint: 'Clique novamente para excluir',
  deleteConfirmation: 'Deseja excluir este provedor?',

  // Connection test
  testLabel: 'Testar conexão',
  retestLabel: 'Testar novamente',
  testingLabel: 'Testando conexão...',
  testFailedFallback: 'Não foi possível testar a conexão agora.',

  // Toasts shown after save, once the automatic test resolves
  createValidatedToast: 'OpenAI conectada e validada com sucesso.',
  updateValidatedToast: 'Provedor atualizado e validado com sucesso.',
  saveInconclusiveToast:
    'Credencial salva, mas não foi possível validar a conexão agora.',

  // Badge labels
  notTestedBadge: 'Não testada',
  testingBadge: 'Testando...',
  connectedBadge: 'Conectado',
  unavailableBadge: 'Indisponível',
  unconfirmedBadge: 'Não confirmado',
  invalidBadge: 'Inválida',
  inconclusiveBadge: 'Não foi possível validar',
  unsupportedBadge: 'Validação indisponível',

  // Fallback messages, only used when the backend result carries no message
  notTestedMessage: 'Credencial ainda não validada.',
  validOperationalMessage: 'OpenAI conectada e pronta para uso.',
  validOperationalFalseFallbackMessage:
    'Credencial válida, mas o provedor não está disponível para uso no momento.',
  validOperationalUnknownMessage:
    'Credencial válida, mas não foi possível confirmar a disponibilidade do provedor.',
  invalidFallbackMessage: 'Não foi possível validar esta credencial.',
  inconclusiveFallbackMessage:
    'Não foi possível concluir o teste agora. Tente novamente.',
};

export const providerOptions = [
  { label: 'OpenAI', value: 'openai', defaultModel: 'gpt-4o-mini' },
  { label: 'Gemini', value: 'gemini', defaultModel: 'gemini-1.5-flash' },
  { label: 'Anthropic', value: 'anthropic', defaultModel: 'claude-3-5-sonnet' },
  {
    label: 'OpenRouter',
    value: 'openrouter',
    defaultModel: 'openai/gpt-4o-mini',
  },
];

export const getDefaultProviderModel = provider => {
  return (
    providerOptions.find(option => option.value === provider)?.defaultModel ||
    ''
  );
};

// A validation/test result as returned by POST provider_credentials/:id/test:
// { success, status, operational, error_code, message, last_tested_at }.
// `message`, when present, is always the sanitized, provider-agnostic text
// the backend already produced for this exact outcome - prefer it over any
// fallback copy here.
export const resolveResultMessage = result => {
  if (!result) return providerFormContent.notTestedMessage;
  if (result.message) return result.message;

  if (result.status === 'valid' && result.operational === true) {
    return providerFormContent.validOperationalMessage;
  }
  if (result.status === 'valid' && result.operational === false) {
    return providerFormContent.validOperationalFalseFallbackMessage;
  }
  if (result.status === 'valid') {
    return providerFormContent.validOperationalUnknownMessage;
  }
  if (result.status === 'invalid') {
    return providerFormContent.invalidFallbackMessage;
  }

  return providerFormContent.inconclusiveFallbackMessage;
};

// testState is local, session-only UI state: { testing, result } | undefined.
// It is never persisted - a page reload falls back to the credential's
// persisted `status` below, which is exactly what create/update/test write.
export const resolveCredentialBadge = (credential, testState) => {
  if (testState?.testing) {
    return { color: 'blue', label: providerFormContent.testingBadge };
  }

  const result = testState?.result;
  if (result) {
    if (result.error_code === 'unsupported_provider') {
      return { color: 'slate', label: providerFormContent.unsupportedBadge };
    }
    if (result.status === 'valid' && result.operational === true) {
      return { color: 'teal', label: providerFormContent.connectedBadge };
    }
    if (result.status === 'valid' && result.operational === false) {
      return { color: 'amber', label: providerFormContent.unavailableBadge };
    }
    if (result.status === 'valid') {
      return { color: 'amber', label: providerFormContent.unconfirmedBadge };
    }
    if (result.status === 'invalid') {
      return { color: 'ruby', label: providerFormContent.invalidBadge };
    }
    return { color: 'amber', label: providerFormContent.inconclusiveBadge };
  }

  if (credential.status === 'valid') {
    return { color: 'teal', label: providerFormContent.connectedBadge };
  }
  if (credential.status === 'invalid') {
    return { color: 'ruby', label: providerFormContent.invalidBadge };
  }
  return { color: 'slate', label: providerFormContent.notTestedBadge };
};

export const resolveCredentialMessage = (credential, testState) => {
  if (testState?.testing) return providerFormContent.testingLabel;
  if (testState?.result) return resolveResultMessage(testState.result);

  if (credential.status === 'valid') {
    return providerFormContent.validOperationalUnknownMessage;
  }
  if (credential.status === 'invalid') {
    return providerFormContent.invalidFallbackMessage;
  }
  return providerFormContent.notTestedMessage;
};

// The toast shown right after a create/update save, once the automatic
// re-test resolves. Deliberately narrower than resolveResultMessage: the
// operational=true/false/nil nuance stays on the persistent card message,
// the toast only needs to say whether the save is actually trustworthy.
export const resolveSaveToastMessage = (result, { isUpdate } = {}) => {
  if (result.status === 'valid') {
    return isUpdate
      ? providerFormContent.updateValidatedToast
      : providerFormContent.createValidatedToast;
  }
  if (result.status === 'invalid') {
    return result.message || providerFormContent.invalidFallbackMessage;
  }
  return result.message || providerFormContent.saveInconclusiveToast;
};
